import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { SHOWROOM } from "@/data/projects";
import { MediaSlot, Reveal } from "@/components/work/MediaSlot";

const EASE = [0.16, 1, 0.3, 1];

// Composed flagship stage visual — built only from a project's real captured
// media (data-driven via project.stageMedia; other projects keep single frame).
// The curtain reveal observes the UN-clipped root and clips an inner frame:
// an IntersectionObserver target clipped to zero area by its own clip-path
// never reports intersection, so whileInView on the clipped node deadlocks.
function StageMediaComposition({ project, mediaYSlow, mediaYFast }) {
  const { desktop, mobile, detail } = project.stageMedia;
  const rootRef = useRef(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });
  return (
    <div
      ref={rootRef}
      className="relative pb-8 pr-2 md:pb-14 md:pr-6"
      data-testid={`stage-media-composition-${project.slug}`}
    >
      <motion.div
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={inView ? { clipPath: "inset(0 0 0% 0)" } : {}}
        transition={{ duration: 1.2, ease: EASE }}
      >
        <Link
          to={`/work/${project.slug}`}
          aria-label={`Open ${project.name}`}
          data-testid={`stage-media-link-${project.slug}`}
          className="block"
        >
          <div className="relative aspect-[16/10] overflow-hidden border border-white/10 bg-surface">
            <img
              src={desktop}
              alt={`${project.name} desktop experience`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-cinematic hover:scale-[1.015]"
            />
          </div>
        </Link>
      </motion.div>
      {detail && (
        <motion.div
          style={{ y: mediaYFast }}
          className="pointer-events-none absolute -bottom-1 -left-1 w-[42%] border border-white/15 bg-obsidian shadow-2xl md:-bottom-4 md:-left-8"
        >
          <img
            src={detail}
            alt={`${project.name} customer interaction`}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        </motion.div>
      )}
      {mobile && (
        <motion.div
          style={{ y: mediaYSlow }}
          className="pointer-events-none absolute -bottom-4 right-0 w-[24%] border border-white/15 bg-obsidian shadow-2xl md:-bottom-10 md:-right-2"
        >
          <img
            src={mobile}
            alt={`${project.name} mobile experience`}
            loading="lazy"
            className="aspect-[9/16] w-full object-cover"
          />
        </motion.div>
      )}
    </div>
  );
}

function Stage({ project, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const mediaYSlow = useTransform(scrollYProgress, [0, 1], ["-11%", "11%"]);
  const mediaYFast = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const reversed = index % 2 === 1;

  return (
    <section
      ref={ref}
      data-testid={`work-project-${project.slug}`}
      className="relative border-t border-white/10"
    >
      <div className="mx-auto grid max-w-[1500px] items-center gap-10 px-5 py-20 md:grid-cols-12 md:gap-14 md:py-32">
        {/* text column */}
        <div className={`md:col-span-5 ${reversed ? "md:order-2" : ""}`}>
          <Reveal>
            <p className="flex flex-col gap-3 text-[10px] font-medium uppercase tracking-[0.4em] text-fog md:flex-row md:items-baseline md:gap-4 md:text-xs">
              <span className="font-display text-4xl font-semibold tracking-[-0.02em] text-outline-strong md:text-6xl">
                {String(index + 1).padStart(2, "0")}
              </span>
              {project.category}
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[0.98] tracking-[-0.03em] md:text-6xl">
              {project.name}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-4 font-display text-xl leading-snug text-fog md:text-2xl">
              {project.headline}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-fog md:text-base">
              {project.description}
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <ul className="mt-8 flex max-w-md flex-wrap gap-2">
              {project.capabilities.slice(0, 4).map((cap) => (
                <li
                  key={cap}
                  className="border border-white/15 px-3 py-1.5 text-[11px] tracking-wide text-fog"
                >
                  {cap}
                </li>
              ))}
              {project.capabilities.length > 4 && (
                <li className="border border-white/15 px-3 py-1.5 text-[11px] tracking-wide text-fog/60">
                  +{project.capabilities.length - 4} more
                </li>
              )}
            </ul>
          </Reveal>
          <Reveal delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={`/work/${project.slug}`}
                data-testid={`explore-${project.slug}`}
                className="group flex items-center gap-2 border border-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 ease-cinematic hover:bg-white hover:text-obsidian"
              >
                Explore the Build
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5" />
              </Link>
              {project.liveUrl || project.demoUrl ? (
                <a
                  href={project.liveUrl || project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`demo-${project.slug}`}
                  className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-fog transition-colors duration-300 hover:text-white"
                >
                  {project.liveUrl ? "Experience the Site" : "View Live Demo"}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ) : (
                <span
                  data-testid={`demo-${project.slug}`}
                  className="text-xs uppercase tracking-[0.25em] text-fog/50"
                >
                  {project.statusLabel}
                </span>
              )}
            </div>
          </Reveal>
          {project.purchaseEligible && (
            <Reveal delay={0.38}>
              <Link
                to={`/work/${project.slug}#make-yours`}
                data-testid={`make-yours-${project.slug}`}
                className="group mt-8 flex w-fit items-center gap-4 border-t border-white/15 pt-5"
              >
                <span className="text-[10px] uppercase tracking-[0.35em] text-fog/60">
                  Want it?
                </span>
                <span className="font-display text-lg font-medium tracking-[-0.01em] text-white underline decoration-white/30 underline-offset-8 transition-colors duration-500 group-hover:decoration-white md:text-xl">
                  Make it yours
                </span>
                <ArrowRight className="h-4 w-4 text-fog transition-all duration-500 ease-cinematic group-hover:translate-x-1 group-hover:text-white" />
              </Link>
            </Reveal>
          )}
        </div>

        {/* media column — the work itself is the hero */}
        <div className={`relative md:col-span-7 ${reversed ? "md:order-1" : ""}`}>
          {project.stageMedia ? (
            <motion.div style={{ y: mediaY }}>
              <StageMediaComposition
                project={project}
                mediaYSlow={mediaYSlow}
                mediaYFast={mediaYFast}
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0)" }}
              whileInView={{ clipPath: "inset(0 0 0% 0)" }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 1.2, ease: EASE }}
            >
              <motion.div style={{ y: mediaY }}>
                <Link
                  to={`/work/${project.slug}`}
                  aria-label={`Open ${project.name}`}
                  data-testid={`stage-media-link-${project.slug}`}
                  className="block"
                >
                  <MediaSlot
                    label="Hero media"
                    src={project.media.hero}
                    ratio="aspect-[16/10]"
                    testId={`stage-media-${project.slug}`}
                    className="transition-transform duration-700 ease-cinematic hover:scale-[1.015]"
                  />
                </Link>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Work() {
  return (
    <div data-testid="work-page" className="pb-32 pt-36 md:pt-48">
      <header className="mx-auto max-w-[1500px] px-5 md:px-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs"
        >
          Work
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: EASE }}
          className="mt-6 font-display text-5xl font-medium leading-[0.95] tracking-[-0.03em] md:text-8xl"
        >
          The showroom.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.25, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-fog md:text-lg"
        >
          Working digital products — websites, tools, and the business systems behind them. See
          it, use it, then make it yours.
        </motion.p>
      </header>

      <div className="mt-16 md:mt-24">
        {SHOWROOM.map((p, i) => (
          <Stage key={p.slug} project={p} index={i} />
        ))}
      </div>

      {/* the other path */}
      <section className="mx-auto mt-24 max-w-[1500px] px-5 md:mt-36 md:px-16">
        <Reveal>
          <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
            Nothing here quite fits?
          </p>
          <Link
            to="/start"
            data-testid="work-custom-link"
            className="group mt-6 flex items-end justify-between gap-8 border-y border-white/10 py-10 transition-colors duration-700 ease-cinematic hover:bg-white md:py-14"
          >
            <span className="font-display text-3xl font-medium leading-[1.02] tracking-[-0.02em] transition-colors duration-700 group-hover:text-obsidian md:text-6xl">
              Build something custom
            </span>
            <ArrowUpRight className="h-8 w-8 shrink-0 transition-all duration-500 ease-cinematic group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-obsidian md:h-12 md:w-12" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
